"use client";

import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  questions,
  stageMeta,
  type BlockToken,
  type Question,
  type SkillTag,
} from "./questions";

type View = "home" | "quiz" | "summary" | "review";
type ReviewFilter = "all" | "practice" | "correct" | "saved";

interface QueueItem {
  key: string;
  questionId: string;
  review: boolean;
}

interface AnswerRecord {
  attempts: number;
  mistakes: number;
  latestCorrect: boolean;
  lastAnswer: string | string[];
}

interface FeedbackState {
  isCorrect: boolean;
  scheduledReview: boolean;
}

interface StudyDayRecord {
  completed: boolean;
  kind: "lesson" | "review";
  label: string;
  mistakeIds: string[];
}

interface SavedProgress {
  version: 1 | 2 | 3;
  started: boolean;
  completed: boolean;
  position: number;
  queue: QueueItem[];
  answers: Record<string, AnswerRecord>;
  bookmarks: string[];
  autoAudio: boolean;
  dailyGoal?: number;
  activeDate?: string;
  history?: Record<string, StudyDayRecord>;
  reviewPool?: string[];
}

interface AuthUser {
  id: number;
  name: string;
}

type AuthState = "checking" | "required" | "ready";
type SyncState = "saved" | "saving" | "error";

const STORAGE_KEY = "malitgi-day-1-v1";
const STORAGE_USER_KEY = "malitgi-user-id-v1";
const DAILY_GOAL_OPTIONS = [30, 40, 50] as const;
const DEFAULT_DAILY_GOAL = 30;
const STUDY_START_DATE = new Date(2026, 9, 6);
const STUDY_END_DATE = new Date(2026, 9, 31);

class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function apiRequest<T>(path: string, options: RequestInit = {}) {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...options,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...options.headers,
    },
  });
  const payload = await response.json().catch(() => ({})) as T & { error?: string };
  if (!response.ok) {
    throw new ApiError(payload.error ?? "The server could not complete the request.", response.status);
  }
  return payload;
}

function readLocalProgress() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as SavedProgress;
    return saved.version === 1 || saved.version === 2 || saved.version === 3 ? saved : null;
  } catch {
    window.localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function normalizeDailyGoal(goal: number) {
  if (goal >= 50) return 50;
  if (goal >= 40) return 40;
  return 30;
}

function dateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function isWeekendDate(date: Date) {
  return date.getDay() === 0 || date.getDay() === 6;
}

function getScheduleLabel(date: Date) {
  const key = dateKey(date);
  if (key < dateKey(STUDY_START_DATE) || key > dateKey(STUDY_END_DATE)) return null;

  let dayCount = 0;
  let reviewCount = 0;
  const cursor = new Date(STUDY_START_DATE);

  while (dateKey(cursor) <= key) {
    if (isWeekendDate(cursor)) reviewCount += 1;
    else dayCount += 1;
    cursor.setDate(cursor.getDate() + 1);
  }

  return isWeekendDate(date) ? `REVIEW ${reviewCount}` : `DAY ${dayCount}`;
}

function createMonthDays(date: Date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const totalDays = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: totalDays }, (_, index) => new Date(year, month, index + 1));
}

function createBaseQueue(goal: number): QueueItem[] {
  const safeGoal = normalizeDailyGoal(goal);
  const basePerStage = Math.floor(safeGoal / 3);
  const remainder = safeGoal % 3;

  return ([1, 2, 3] as const).flatMap((stage, index) =>
    questions
      .filter((question) => question.stage === stage)
      .slice(0, basePerStage + (index < remainder ? 1 : 0))
      .map((question) => ({
        key: question.id,
        questionId: question.id,
        review: false,
      })),
  );
}

function createReviewQueue(questionIds: string[], goal: number): QueueItem[] {
  return questionIds.slice(0, goal).map((questionId) => ({
    key: `weekend-${questionId}`,
    questionId,
    review: false,
  }));
}

function getGrammarGuide(question: Question) {
  const answer = Array.isArray(question.answer) ? question.correctSentence : question.answer;

  if (answer === "대부분") {
    return {
      structureEn: "[group]의 대부분 + 은/이 + description",
      tipEn: "‘대부분’ means ‘most of’ a group, so it describes a large proportion. Unlike ‘아마’, it does not express uncertainty or a guess.",
      structureKo: "[집단]의 대부분 + 은/이 + 설명",
      tipKo: "‘대부분’은 사람이나 사물의 큰 비율을 가리켜요. 불확실한 추측을 나타내는 ‘아마’와는 역할이 달라요.",
    };
  }
  if (answer === "거의") {
    return {
      structureEn: "object + 거의 다 + completion verb",
      tipEn: "Use ‘거의’ when something is close to complete or close to the whole amount. Use ‘대략’ instead when estimating a number, time, or distance.",
      structureKo: "목적어 + 거의 다 + 완료 동사",
      tipKo: "끝이나 전체에 가까운 상태에는 ‘거의’를 써요. ‘대략’은 시간·수량을 어림잡을 때 사용해요.",
    };
  }
  if (answer === "아마" || question.correctSentence.includes("아마 ")) {
    return {
      structureEn: "아마 + predicted situation + -(으)ㄹ 것 같아요",
      tipEn: "‘아마’ marks probability, not proportion. Pairing it with ‘것 같아요’ clearly frames the sentence as an uncertain prediction.",
      structureKo: "아마 + 예상 내용 + -(으)ㄹ 것 같아요",
      tipKo: "‘아마’는 사실의 비율이 아니라 가능성을 말해요. 뒤의 ‘것 같아요’와 함께 쓰면 추측이라는 뜻이 분명해져요.",
    };
  }
  if (answer === "보통") {
    return {
      structureEn: "subject + time/situation + 보통 + repeated action",
      tipEn: "Use ‘보통’ for a usual habit or general tendency. It does not describe a one-time prediction or an approximate quantity.",
      structureKo: "주어 + 시간/상황 + 보통 + 반복 행동",
      tipKo: "평소 습관이나 일반적인 경향을 말할 때 사용해요. 한 번의 예상이나 수량을 나타내지는 않아요.",
    };
  }
  if (answer === "약") {
    return {
      structureEn: "약 + number/quantity + unit",
      tipEn: "Place ‘약’ directly before a number to make the quantity approximate. ‘아마’ modifies the likelihood of the whole statement instead.",
      structureKo: "약 + 숫자/수량 + 단위",
      tipKo: "‘약’은 숫자 바로 앞에서 대략적인 수량을 만들어요. ‘아마’는 숫자를 꾸미지 않고 문장 전체의 가능성을 나타내요.",
    };
  }
  if (answer === "대략") {
    return {
      structureEn: "대략 + time/distance/amount + 정도",
      tipEn: "Use ‘대략’ to estimate a measurable value. When you mean that an action is nearly complete, ‘거의’ is the natural choice.",
      structureKo: "대략 + 시간/거리/금액 + 정도",
      tipKo: "정확하지 않은 수치를 어림잡을 때 사용해요. 완료에 가까운 상태를 말할 때는 ‘거의’가 알맞아요.",
    };
  }
  if (question.correctSentence.includes("려고 했는데")) {
    return {
      structureEn: "verb stem + -(으)려고 했는데 + reason it did not happen",
      tipEn: "This pattern contrasts an earlier intention with the actual result. The second clause usually gives the obstacle or the opposite outcome.",
      structureKo: "동사 어간 + -(으)려고 했는데 + 이루어지지 않은 이유",
      tipKo: "하려던 의도와 실제 결과가 달랐다는 것을 보여 줘요. 뒤 절에는 실행하지 못한 이유나 반대 결과가 와요.",
    };
  }
  if (question.correctSentence.includes("지 않으려고")) {
    return {
      structureEn: "action to avoid + -지 않으려고 + action taken",
      tipEn: "The second action is done in order to avoid the first result. This expresses purpose, unlike ‘-아서/어서’, which gives a cause or reason.",
      structureKo: "피하려는 행동 + -지 않으려고 + 실제로 한 행동",
      tipKo: "뒤 행동을 한 목적이 ‘앞의 일을 피하기 위해서’라는 뜻이에요. 원인을 나타내는 ‘-아서/어서’와 구별해요.",
    };
  }
  if (question.correctSentence.includes("고 나서")) {
    return {
      structureEn: "first action + -고 나서 + next action",
      tipEn: "The first action must finish before the second begins. Keep the sentence blocks in the same order as the real-world timeline.",
      structureKo: "먼저 한 동작 + -고 나서 + 다음 동작",
      tipKo: "앞 동작이 완전히 끝난 뒤에 다음 동작이 이어져요. 두 블록의 실제 시간 순서가 바뀌지 않도록 조립해야 해요.",
    };
  }
  if (question.correctSentence.includes("다음에")) {
    return {
      structureEn: "verb + -(으)ㄴ 다음에 + next action or plan",
      tipEn: "Mark the first action as completed, then finish the sentence with the tense or plan form that belongs to the next action.",
      structureKo: "동사 + -(으)ㄴ 다음에 + 다음 행동/계획",
      tipKo: "앞 행동의 완료를 먼저 표시한 뒤, 다음 행동의 시제나 계획 표현으로 문장을 끝내요.",
    };
  }
  if (question.correctSentence.includes("뒤에")) {
    return {
      structureEn: "verb + -(으)ㄴ 뒤에 + next action or intention",
      tipEn: "The action before ‘뒤에’ is completed first. The final clause uses forms such as ‘-(으)ㄹ게요’ or ‘-(으)려고 해요’ to show what follows.",
      structureKo: "동사 + -(으)ㄴ 뒤에 + 다음 행동/의도",
      tipKo: "‘뒤에’ 앞에는 완료된 동작이 와요. 뒤 절의 ‘-(으)ㄹ게요/-(으)려고 해요’가 문장 전체를 마무리해요.",
    };
  }
  if (question.correctSentence.includes("려고")) {
    return {
      structureEn: "purpose or plan + -(으)려고 + action / 하다",
      tipEn: "Use this form to explain why an action is done or what someone plans to do. It expresses intention, not only chronological order.",
      structureKo: "목적 또는 계획 + -(으)려고 + 행동/하다",
      tipKo: "뒤의 행동을 왜 하는지, 또는 앞으로 무엇을 할 계획인지 나타내요. 단순한 순서 표현과 구별해요.",
    };
  }
  if (question.correctSentence.includes("기로 했어요")) {
    return {
      structureEn: "verb stem + -기로 했어요",
      tipEn: "This ending shows an agreement or decision that has already been made. It is stronger and more settled than a plan still under consideration.",
      structureKo: "동사 어간 + -기로 했어요",
      tipKo: "이미 정한 약속이나 결정을 나타내요. 아직 고민 중인 계획보다 결정의 의미가 더 강해요.",
    };
  }
  if (question.correctSentence.includes("하면")) {
    return {
      structureEn: "repeated condition + -(으)면 + habitual action",
      tipEn: "Use this pattern for an action that regularly follows the same condition. It differs from a one-time sequence of completed past events.",
      structureKo: "반복되는 조건 + -(으)면 + 습관적 행동",
      tipKo: "특정 상황이 생길 때마다 반복하는 행동을 연결해요. 한 번만 일어난 과거 순서와는 달라요.",
    };
  }
  return {
    structureEn: "first situation + connector + sentence-final verb",
    tipEn: "Check both the timeline between the actions and the tense of the final verb. These two clues help you build a natural sentence.",
    structureKo: "앞 상황 + 연결 표현 + 문장을 끝내는 동사",
    tipKo: "앞뒤 행동의 시간 관계와 마지막 동사의 시제를 함께 확인하면 자연스럽게 문장을 완성할 수 있어요.",
  };
}

function getMemoryFocus(question: Question) {
  const answer = Array.isArray(question.answer) ? question.correctSentence : question.answer;

  if (!Array.isArray(question.answer)) {
    return {
      en: `Remember ‘${answer}’ as the key choice in this sentence.`,
      ko: `이 문장에서는 ‘${answer}’을 핵심 선택지로 기억해요.`,
    };
  }
  if (question.correctSentence.includes("고 나서")) {
    return {
      en: "Focus form: -고 나서 (after doing). Attach it to the action that finishes first.",
      ko: "핵심 형태: -고 나서. 먼저 끝나는 행동에 붙여요.",
    };
  }
  if (question.correctSentence.includes("다음에")) {
    return {
      en: "Focus form: -(으)ㄴ 다음에 (after/next). The first verb is marked as completed.",
      ko: "핵심 형태: -(으)ㄴ 다음에. 먼저 끝난 동사에 붙여요.",
    };
  }
  if (question.correctSentence.includes("뒤에")) {
    return {
      en: "Focus form: -(으)ㄴ 뒤에 (after). Complete the first action before stating the next one.",
      ko: "핵심 형태: -(으)ㄴ 뒤에. 앞 행동의 완료 뒤에 다음 행동을 말해요.",
    };
  }
  if (question.correctSentence.includes("려고")) {
    return {
      en: "Focus form: -(으)려고 (in order to / intending to). Use it for purpose or intention.",
      ko: "핵심 형태: -(으)려고. 목적이나 의도를 나타낼 때 사용해요.",
    };
  }
  if (question.correctSentence.includes("기로 했어요")) {
    return {
      en: "Focus form: -기로 했어요 (decided/agreed to). It signals a settled decision.",
      ko: "핵심 형태: -기로 했어요. 이미 정한 결정이나 약속을 나타내요.",
    };
  }
  if (question.correctSentence.includes("하면")) {
    return {
      en: "Focus form: -(으)면 (when/if). Here it connects a repeated condition with a habit.",
      ko: "핵심 형태: -(으)면. 반복되는 조건과 습관을 연결해요.",
    };
  }

  return {
    en: `Memorize the connector in: ${question.correctSentence}`,
    ko: `정답 문장 속 연결 표현을 통째로 기억해요: ${question.correctSentence}`,
  };
}

const questionMap = new Map(questions.map((question) => [question.id, question]));

function speak(text: string, rate = 0.88) {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = "ko-KR";
  utterance.rate = rate;
  window.speechSynthesis.speak(utterance);
}

function answerLabel(question: Question, value: string | string[]) {
  if (Array.isArray(value)) {
    const blockMap = new Map(question.blocks?.map((block) => [block.id, block.text]));
    return value.map((id) => blockMap.get(id) ?? "").join(" ");
  }
  return value;
}

export function KoreanPracticeApp() {
  const today = new Date();
  const todayKey = dateKey(today);
  const isWeekend = isWeekendDate(today);
  const sessionLabel = getScheduleLabel(today) ?? "COURSE COMPLETE";
  const [hydrated, setHydrated] = useState(false);
  const [authState, setAuthState] = useState<AuthState>("checking");
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [loginName, setLoginName] = useState("");
  const [loginPin, setLoginPin] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginPending, setLoginPending] = useState(false);
  const [syncState, setSyncState] = useState<SyncState>("saved");
  const saveTimer = useRef<number | null>(null);
  const [view, setView] = useState<View>("home");
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [dailyGoal, setDailyGoal] = useState(DEFAULT_DAILY_GOAL);
  const [queue, setQueue] = useState<QueueItem[]>(
    isWeekend ? [] : createBaseQueue(DEFAULT_DAILY_GOAL),
  );
  const [position, setPosition] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerRecord>>({});
  const [bookmarks, setBookmarks] = useState<string[]>([]);
  const [history, setHistory] = useState<Record<string, StudyDayRecord>>({});
  const [reviewPool, setReviewPool] = useState<string[]>([]);
  const [autoAudio, setAutoAudio] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [selectedBlocks, setSelectedBlocks] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>("all");
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [notice, setNotice] = useState("");

  function restoreSavedProgress(saved: SavedProgress) {
    const savedGoal = saved.dailyGoal ?? new Set(saved.queue.filter((item) => !item.review).map((item) => item.questionId)).size;
    const nextGoal = normalizeDailyGoal(savedGoal || DEFAULT_DAILY_GOAL);
    const hasSupportedGoal = DAILY_GOAL_OPTIONS.includes(savedGoal as (typeof DAILY_GOAL_OPTIONS)[number]);
    const savedHistory = { ...(saved.history ?? {}) };
    const savedReviewPool = saved.reviewPool ?? Object.entries(saved.answers)
      .filter(([, record]) => record.mistakes > 0)
      .map(([questionId]) => questionId);
    const sameDay = !saved.activeDate || saved.activeDate === todayKey;

    if (sameDay && saved.completed && !savedHistory[todayKey]) {
      savedHistory[todayKey] = {
        completed: true,
        kind: isWeekend ? "review" : "lesson",
        label: sessionLabel,
        mistakeIds: Object.entries(saved.answers)
          .filter(([, record]) => record.mistakes > 0)
          .map(([questionId]) => questionId),
      };
    }

    setDailyGoal(nextGoal);
    setBookmarks(saved.bookmarks);
    setAutoAudio(saved.autoAudio);
    setHistory(savedHistory);
    setReviewPool(savedReviewPool);

    if (sameDay && hasSupportedGoal) {
      setStarted(saved.started);
      setCompleted(saved.completed);
      setPosition(saved.position);
      setQueue(saved.queue.length
        ? saved.queue
        : isWeekend
          ? createReviewQueue(savedReviewPool, nextGoal)
          : createBaseQueue(nextGoal));
      setAnswers(saved.answers);
    } else {
      setQueue(isWeekend
        ? createReviewQueue(savedReviewPool, nextGoal)
        : createBaseQueue(nextGoal));
    }
  }

  function resetProgressState() {
    setView("home");
    setStarted(false);
    setCompleted(false);
    setDailyGoal(DEFAULT_DAILY_GOAL);
    setQueue(isWeekend ? [] : createBaseQueue(DEFAULT_DAILY_GOAL));
    setPosition(0);
    setAnswers({});
    setBookmarks([]);
    setHistory({});
    setReviewPool([]);
    setAutoAudio(false);
  }

  function createSavedProgress(): SavedProgress {
    return {
      version: 3,
      started,
      completed,
      position,
      queue,
      answers,
      bookmarks,
      autoAudio,
      dailyGoal,
      activeDate: todayKey,
      history,
      reviewPool,
    };
  }

  useEffect(() => {
    let active = true;
    const localProgress = readLocalProgress();

    async function initialize() {
      try {
        const session = await apiRequest<{ authenticated: boolean; user?: AuthUser }>("/api/auth/session");
        if (!active) return;

        if (!session.authenticated || !session.user) {
          if (localProgress) restoreSavedProgress(localProgress);
          else if (isWeekend) setQueue([]);
          setAuthState("required");
          return;
        }

        const cloud = await apiRequest<{ progress: SavedProgress | null }>("/api/progress");
        if (!active) return;
        const localOwner = window.localStorage.getItem(STORAGE_USER_KEY);
        if (cloud.progress) {
          restoreSavedProgress(cloud.progress);
        } else if (localProgress && (!localOwner || localOwner === String(session.user.id))) {
          restoreSavedProgress(localProgress);
          await apiRequest("/api/progress", {
            method: "PUT",
            body: JSON.stringify({ progress: localProgress }),
          });
        } else {
          resetProgressState();
        }
        window.localStorage.setItem(STORAGE_USER_KEY, String(session.user.id));
        setAuthUser(session.user);
        setAuthState("ready");
      } catch {
        if (!active) return;
        if (localProgress) restoreSavedProgress(localProgress);
        setLoginError("The save server is temporarily unavailable. Please try again shortly.");
        setAuthState("required");
      } finally {
        if (active) setHydrated(true);
      }
    }

    initialize();

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => undefined);
    }

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!hydrated || authState !== "ready") return;
    const progress = createSavedProgress();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    setSyncState("saving");

    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      apiRequest("/api/progress", {
        method: "PUT",
        body: JSON.stringify({ progress }),
      })
        .then(() => setSyncState("saved"))
        .catch((error: unknown) => {
          setSyncState("error");
          if (error instanceof ApiError && error.status === 401) {
            setAuthUser(null);
            setAuthState("required");
            setLoginError("Your login expired. Please sign in again.");
          }
        });
    }, 450);

    return () => {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    };
  }, [answers, authState, autoAudio, bookmarks, completed, dailyGoal, history, hydrated, position, queue, reviewPool, started, todayKey]);

  async function submitLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!loginName.trim() || !/^\d{4}$/.test(loginPin)) {
      setLoginError("Enter your name and a 4-digit PIN.");
      return;
    }
    setLoginPending(true);
    setLoginError("");
    try {
      const result = await apiRequest<{ user: AuthUser }>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ name: loginName.trim(), pin: loginPin }),
      });
      const cloud = await apiRequest<{ progress: SavedProgress | null }>("/api/progress");
      const localProgress = readLocalProgress();
      const localOwner = window.localStorage.getItem(STORAGE_USER_KEY);

      if (cloud.progress) {
        restoreSavedProgress(cloud.progress);
      } else if (localProgress && (!localOwner || localOwner === String(result.user.id))) {
        restoreSavedProgress(localProgress);
        await apiRequest("/api/progress", {
          method: "PUT",
          body: JSON.stringify({ progress: localProgress }),
        });
      } else {
        resetProgressState();
      }

      window.localStorage.setItem(STORAGE_USER_KEY, String(result.user.id));
      setAuthUser(result.user);
      setLoginPin("");
      setAuthState("ready");
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Could not sign in.");
    } finally {
      setLoginPending(false);
    }
  }

  async function logout() {
    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    try {
      await apiRequest("/api/auth/logout", { method: "POST", body: "{}" });
    } catch {
      // The local sign-out still proceeds when the network is temporarily unavailable.
    } finally {
      setAuthUser(null);
      setLoginName("");
      setLoginPin("");
      setLoginError("");
      setAuthState("required");
    }
  }

  const currentItem = queue[position] ?? queue[queue.length - 1] ?? createBaseQueue(DEFAULT_DAILY_GOAL)[0];
  const currentQuestion = questionMap.get(currentItem.questionId) ?? questions[0];
  const currentStage = stageMeta[currentQuestion.stage];
  const progress = Math.round(((position + (feedback ? 1 : 0)) / queue.length) * 100);

  const activeQuestionIds = useMemo(
    () => new Set(queue.map((item) => item.questionId)),
    [queue],
  );

  const tagMistakes = useMemo(() => {
    const totals = new Map<SkillTag, number>();
    questions.filter((question) => activeQuestionIds.has(question.id)).forEach((question) => {
      const mistakes = answers[question.id]?.mistakes ?? 0;
      totals.set(question.tag, (totals.get(question.tag) ?? 0) + mistakes);
    });
    return [...totals.entries()].sort((a, b) => b[1] - a[1]);
  }, [activeQuestionIds, answers]);

  const practiceCount = questions.filter(
    (question) => activeQuestionIds.has(question.id) && (answers[question.id]?.mistakes ?? 0) > 0,
  ).length;
  const familiarCount = questions.filter(
    (question) =>
      activeQuestionIds.has(question.id) && answers[question.id]?.latestCorrect && answers[question.id]?.mistakes === 0,
  ).length;

  const completedCount = questions.filter((question) => activeQuestionIds.has(question.id) && answers[question.id]).length;
  const grammarGuide = getGrammarGuide(currentQuestion);
  const mistakeQuestions = questions.filter(
    (question) => activeQuestionIds.has(question.id) && (answers[question.id]?.mistakes ?? 0) > 0,
  );
  const focusQuestion = mistakeQuestions[0];
  const focusGuide = focusQuestion ? getGrammarGuide(focusQuestion) : null;
  const memoryFocus = focusQuestion ? getMemoryFocus(focusQuestion) : null;
  const monthDays = createMonthDays(STUDY_START_DATE);

  function beginLesson() {
    if (!started) {
      const nextQueue = isWeekend
        ? createReviewQueue(reviewPool, dailyGoal)
        : createBaseQueue(dailyGoal);
      if (nextQueue.length === 0) {
        setNotice("이번 주에 다시 풀 오답이 아직 없어요.");
        return;
      }
      setQueue(nextQueue);
      setPosition(0);
      setAnswers({});
    }
    setNotice("");
    setStarted(true);
    setView("quiz");
  }

  function updateDailyGoal(value: number) {
    if (started) return;
    const nextGoal = normalizeDailyGoal(value);
    setDailyGoal(nextGoal);
    setQueue(createBaseQueue(nextGoal));
  }

  function openReview(filter: ReviewFilter, questionId?: string) {
    setReviewFilter(filter);
    setSelectedReviewId(questionId ?? null);
    setView("review");
  }

  function submitAnswer(value: string | string[], isCorrect: boolean) {
    if (feedback) return;

    const previous = answers[currentQuestion.id];
    setAnswers((current) => ({
      ...current,
      [currentQuestion.id]: {
        attempts: (previous?.attempts ?? 0) + 1,
        mistakes: (previous?.mistakes ?? 0) + (isCorrect ? 0 : 1),
        latestCorrect: isCorrect,
        lastAnswer: value,
      },
    }));

    let scheduledReview = false;
    if (!isCorrect && !currentItem.review) {
      const alreadyQueued = queue
        .slice(position + 1)
        .some((item) => item.questionId === currentQuestion.id && item.review);

      if (!alreadyQueued) {
        scheduledReview = true;
        setQueue((current) => {
          const next = [...current];
          const insertionIndex = Math.min(position + 4, next.length);
          next.splice(insertionIndex, 0, {
            key: `${currentQuestion.id}-review-${position}`,
            questionId: currentQuestion.id,
            review: true,
          });
          return next;
        });
      }
    }

    setFeedback({ isCorrect, scheduledReview });
    if (autoAudio) speak(currentQuestion.correctSentence);
  }

  function selectOption(option: string) {
    setSelectedOption(option);
    submitAnswer(option, option === currentQuestion.answer);
  }

  function addBlock(block: BlockToken) {
    if (feedback || selectedBlocks.includes(block.id)) return;
    setSelectedBlocks((current) => [...current, block.id]);
  }

  function removeBlock(blockId: string) {
    if (feedback) return;
    setSelectedBlocks((current) => current.filter((id) => id !== blockId));
  }

  function checkBlocks() {
    const correct = currentQuestion.answer as string[];
    const isCorrect =
      selectedBlocks.length === correct.length &&
      selectedBlocks.every((value, index) => value === correct[index]);
    submitAnswer(selectedBlocks, isCorrect);
  }

  function nextQuestion() {
    setFeedback(null);
    setSelectedOption(null);
    setSelectedBlocks([]);

    if (position + 1 >= queue.length) {
      const mistakeIds = [...activeQuestionIds].filter((questionId) => (answers[questionId]?.mistakes ?? 0) > 0);
      setHistory((current) => ({
        ...current,
        [todayKey]: {
          completed: true,
          kind: isWeekend ? "review" : "lesson",
          label: sessionLabel,
          mistakeIds,
        },
      }));
      setReviewPool((current) => {
        if (isWeekend) {
          const practicedIds = new Set(activeQuestionIds);
          const retryIds = new Set(mistakeIds);
          return current.filter((questionId) => !practicedIds.has(questionId) || retryIds.has(questionId));
        }
        return [...new Set([...current, ...mistakeIds])];
      });
      setCompleted(true);
      setView("summary");
      return;
    }
    setPosition((current) => current + 1);
  }

  function toggleBookmark(questionId: string) {
    setBookmarks((current) =>
      current.includes(questionId)
        ? current.filter((id) => id !== questionId)
        : [...current, questionId],
    );
  }

  function resetLesson() {
    const shouldReset = window.confirm(
      `${sessionLabel} 진행 기록을 지우고 오늘 학습을 처음부터 다시 시작할까요?`,
    );
    if (!shouldReset) return;
    setView("home");
    setStarted(false);
    setCompleted(false);
    setQueue(isWeekend ? createReviewQueue(reviewPool, dailyGoal) : createBaseQueue(dailyGoal));
    setPosition(0);
    setAnswers({});
    setHistory((current) => {
      const next = { ...current };
      delete next[todayKey];
      return next;
    });
    setSelectedBlocks([]);
    setSelectedOption(null);
    setFeedback(null);
    setSelectedReviewId(null);
    setNotice("");
  }

  async function shareResult() {
    const topTag = tagMistakes.find(([, count]) => count > 0)?.[0];
    const message = topTag
      ? `오늘 한국어 훈련을 마쳤어요. 내일은 ‘${topTag}’ 표현을 조금 더 연습할 거예요.`
      : isWeekend
        ? `오늘 오답 복습을 마쳤어요. ${completedCount}문장을 다시 말해 봤어요.`
        : `오늘 한국어 훈련을 마쳤어요. ${dailyGoal}문항을 차분하게 모두 익혔어요.`;

    try {
      if (navigator.share) {
        await navigator.share({ title: `Abby Korean Study · ${sessionLabel}`, text: message });
      } else {
        await navigator.clipboard.writeText(message);
        setNotice("학습 요약을 복사했어요.");
      }
    } catch {
      setNotice("공유를 취소했어요.");
    }
  }

  if (!hydrated || authState === "checking") {
    return (
      <main className="app-shell loading-shell" aria-busy="true">
        <div className="loading-mark" aria-hidden="true">ㅁ</div>
        <p>오늘의 문장을 준비하고 있어요</p>
      </main>
    );
  }

  if (authState === "required") {
    return (
      <main className="login-shell">
        <header className="login-brand" aria-label="Abby Korean Study">
          <span className="brand-mark">가</span>
          <div>
            <strong>Abby Korean Study</strong>
            <span>Daily Korean sentence practice</span>
          </div>
        </header>
        <section className="login-modal" role="dialog" aria-modal="true" aria-labelledby="login-title">
          <span className="card-label">Save your progress · 학습 기록 저장</span>
          <h1 id="login-title">Welcome,<br />What should we call you?</h1>
          <p>Enter a name and a 4-digit PIN.<br />We will keep you signed in and save your history.</p>
          <form onSubmit={submitLogin}>
            <label>
              <span>Name · 이름</span>
              <input
                value={loginName}
                onChange={(event) => setLoginName(event.target.value)}
                maxLength={24}
                autoComplete="username"
                placeholder="Abby"
                autoFocus
              />
            </label>
            <label>
              <span>4-digit PIN · 숫자 비밀번호</span>
              <input
                value={loginPin}
                onChange={(event) => setLoginPin(event.target.value.replace(/\D/g, "").slice(0, 4))}
                inputMode="numeric"
                pattern="[0-9]{4}"
                maxLength={4}
                autoComplete="current-password"
                placeholder="••••"
                type="password"
              />
            </label>
            {loginError && <p className="login-error" role="alert">{loginError}</p>}
            <button className="primary-button large" disabled={loginPending}>
              {loginPending ? "Saving…" : "Start learning · 학습 시작"}
            </button>
          </form>
        </section>
      </main>
    );
  }

  if (view === "quiz") {
    return (
      <main className="app-shell quiz-shell">
        <header className="quiz-header">
          <button className="text-button" onClick={() => setView("home")}>나가기</button>
          <div className="progress-copy" aria-live="polite">
            <strong>{position + 1}</strong>
            <span>/ {queue.length}</span>
          </div>
          <button
            className={`icon-button ${bookmarks.includes(currentQuestion.id) ? "is-active" : ""}`}
            onClick={() => toggleBookmark(currentQuestion.id)}
            aria-label={bookmarks.includes(currentQuestion.id) ? "저장 취소" : "문장 저장"}
            aria-pressed={bookmarks.includes(currentQuestion.id)}
          >
            {bookmarks.includes(currentQuestion.id) ? "★" : "☆"}
          </button>
        </header>

        <div
          className="progress-track"
          role="progressbar"
          aria-label="오늘의 학습 진행률"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
        >
          <span style={{ width: `${progress}%` }} />
        </div>

        <section className="question-area" aria-labelledby="question-title">
          <div className="round-row">
            <span className={`stage-chip ${currentStage.tone}`}>
              {isWeekend ? `Review · ${currentQuestion.tag}` : `Round ${currentQuestion.stage} · ${currentStage.label}`}
            </span>
            {currentItem.review && <span className="review-chip">한 번 더</span>}
          </div>

          <p className="question-kicker">
            {currentQuestion.type === "blocks" ? currentQuestion.prompt : currentStage.short}
          </p>
          <h1 id="question-title">
            {currentQuestion.type === "blocks" && currentQuestion.context
              ? currentQuestion.context
              : currentQuestion.prompt}
          </h1>
          {currentQuestion.type !== "blocks" && currentQuestion.context && (
            <p className="question-context">{currentQuestion.context}</p>
          )}

          {currentQuestion.type === "choice" && (
            <div className="choice-list" role="group" aria-label="답 선택">
              {currentQuestion.options?.map((option, index) => {
                const isSelected = selectedOption === option;
                const isAnswer = feedback && option === currentQuestion.answer;
                const isWrong = feedback && isSelected && !feedback.isCorrect;
                return (
                  <button
                    key={option}
                    className={`choice-button ${isSelected ? "selected" : ""} ${isAnswer ? "correct" : ""} ${isWrong ? "wrong" : ""}`}
                    onClick={() => selectOption(option)}
                    disabled={Boolean(feedback)}
                  >
                    <span className="choice-index">{String.fromCharCode(65 + index)}</span>
                    <span>{option}</span>
                  </button>
                );
              })}
            </div>
          )}

          {currentQuestion.type === "blocks" && (
            <div className="blocks-practice">
              <div className="answer-slot" aria-label="조립한 문장">
                {selectedBlocks.length === 0 ? (
                  <span className="slot-placeholder">아래 조각을 차례로 눌러 보세요</span>
                ) : (
                  selectedBlocks.map((blockId) => {
                    const block = currentQuestion.blocks?.find((item) => item.id === blockId);
                    return (
                      <button
                        key={blockId}
                        onClick={() => removeBlock(blockId)}
                        disabled={Boolean(feedback)}
                        className="selected-block"
                      >
                        {block?.text}
                      </button>
                    );
                  })
                )}
              </div>
              <div className="block-bank" aria-label="사용할 문장 조각">
                {currentQuestion.blocks?.map((block) => (
                  <button
                    key={block.id}
                    onClick={() => addBlock(block)}
                    disabled={selectedBlocks.includes(block.id) || Boolean(feedback)}
                    className="block-token"
                  >
                    {block.text}
                  </button>
                ))}
              </div>
              {!feedback && (
                <button
                  className="primary-button check-button"
                  onClick={checkBlocks}
                  disabled={selectedBlocks.length !== currentQuestion.blocks?.length}
                >
                  문장 확인하기
                </button>
              )}
            </div>
          )}
        </section>

        {feedback && (
          <section
            className={`feedback-sheet ${feedback.isCorrect ? "success" : "retry"}`}
            aria-live="polite"
          >
            <div className="feedback-heading">
              <div>
                <span className="feedback-label">
                  {feedback.isCorrect ? "좋아요, 자연스러워요" : "여기를 다시 볼까요?"}
                </span>
                <strong>{currentQuestion.correctSentence}</strong>
              </div>
              <button
                className="sound-button"
                onClick={() => speak(currentQuestion.correctSentence)}
                aria-label="정답 문장 듣기"
              >
                듣기
              </button>
            </div>
            {!feedback.isCorrect && selectedOption && (
              <p className="your-answer">내가 고른 문장: {selectedOption}</p>
            )}
            {!feedback.isCorrect && selectedBlocks.length > 0 && (
              <p className="your-answer">
                내가 만든 문장: {answerLabel(currentQuestion, selectedBlocks)}
              </p>
            )}
            <div className="feedback-details">
              <div>
                <span>Why this answer? · 선택 이유</span>
                <p>{currentQuestion.explanationEn}</p>
                <p className="korean-translation" lang="ko">한국어 · {currentQuestion.explanationKo}</p>
              </div>
              <div>
                <span>Sentence pattern · 문장 구조</span>
                <p className="structure-line">{grammarGuide.structureEn}</p>
                <p className="korean-translation structure-line" lang="ko">한국어 · {grammarGuide.structureKo}</p>
              </div>
              <div>
                <span>What to notice · 주의할 점</span>
                <p>{grammarGuide.tipEn}</p>
                <p className="korean-translation" lang="ko">한국어 · {grammarGuide.tipKo}</p>
              </div>
            </div>
            {feedback.scheduledReview && (
              <p className="repeat-note">잠시 후 같은 구조를 한 번 더 만나요.</p>
            )}
            <button className="primary-button" onClick={nextQuestion}>
              {position + 1 >= queue.length ? "오늘의 기록 보기" : "다음 문장"}
            </button>
          </section>
        )}
      </main>
    );
  }

  if (view === "summary") {
    const strongestTag = tagMistakes.find(([, count]) => count === 0)?.[0];
    const firstWeekdayOffset = (monthDays[0].getDay() + 6) % 7;

    return (
      <main className="app-shell summary-shell">
        <header className="brand-header">
          <button className="brand" onClick={() => setView("home")}>
            <span className="brand-mark">가</span>
            <span>Abby Korean Study</span>
          </button>
          <span className="day-label">{sessionLabel}</span>
        </header>

        <section className="completion-hero">
          <span className="completion-icon" aria-hidden="true">✓</span>
          <p className="eyebrow">오늘의 연습 완료</p>
          <h1>
            {isWeekend
              ? <>{completedCount}개의 오답 문장을<br />다시 풀었어요.</>
              : <>{dailyGoal}문항을<br />끝까지 풀었어요.</>}
          </h1>
          <p>점수 대신 오늘 발견한 말하기 습관을 정리했어요.</p>
        </section>

        <section className="summary-grid" aria-label="오늘의 학습 요약">
          <button onClick={() => openReview("all")}>
            <strong>{completedCount}</strong><span>학습한 문장</span><small>전체 보기</small>
          </button>
          <button onClick={() => openReview("correct")}>
            <strong>{familiarCount}</strong><span>맞은 문장</span><small>선택해 보기</small>
          </button>
          <button onClick={() => openReview("practice")}>
            <strong>{practiceCount}</strong><span>틀린 문장</span><small>선택해 보기</small>
          </button>
        </section>

        <section className="month-calendar" aria-label={`${STUDY_START_DATE.getFullYear()}년 ${STUDY_START_DATE.getMonth() + 1}월 학습 달력`}>
          <div className="calendar-heading">
            <div>
              <span className="card-label">MONTHLY STUDY · 월간 학습</span>
              <h2>{STUDY_START_DATE.getFullYear()}년 {STUDY_START_DATE.getMonth() + 1}월</h2>
            </div>
            <div className="calendar-legend">
              <span><i className="done" />완료</span>
              <span><i />미완료</span>
            </div>
          </div>
          <div className="calendar-weekdays" aria-hidden="true">
            {['월', '화', '수', '목', '금', '토', '일'].map((weekday) => <span key={weekday}>{weekday}</span>)}
          </div>
          <div className="calendar-grid">
            {Array.from({ length: firstWeekdayOffset }, (_, index) => (
              <span className="calendar-blank" key={`blank-${index}`} />
            ))}
            {monthDays.map((date) => {
              const key = dateKey(date);
              const record = history[key];
              const isToday = key === todayKey;
              const isPast = key < todayKey;
              const label = getScheduleLabel(date);
              const done = Boolean(label && record?.completed);
              return (
                <div
                  className={`calendar-day ${done ? "completed" : "pending"} ${isToday ? "today" : ""} ${isPast && !done ? "locked" : ""} ${isWeekendDate(date) ? "weekend" : ""} ${label ? "" : "outside-course"}`}
                  key={key}
                  aria-label={label
                    ? `${date.getDate()}일 ${label}, ${done ? "완료" : isPast ? "미완료, 다시 풀 수 없음" : "미완료"}`
                    : `${date.getDate()}일, 학습 시작 전`}
                >
                  <span className="calendar-date">{date.getDate()}</span>
                  <strong>{label?.toLowerCase().replace(" ", "") ?? ""}</strong>
                  <i aria-hidden="true">{done ? "✓" : ""}</i>
                </div>
              );
            })}
          </div>
          <p className="calendar-note">지난 날짜는 다시 열 수 없고, 토·일요일에는 모아 둔 오답만 복습해요.</p>
        </section>

        <section className="insight-card">
          <span className="card-label">TODAY'S OBSERVATION · 오늘의 관찰</span>
          {focusQuestion && focusGuide && memoryFocus ? (
            <>
              <h2>Three things to remember from today’s mistakes</h2>
              <p className="insight-sentence">{focusQuestion.correctSentence}</p>
              <div className="insight-study-points">
                <div>
                  <span>Sentence pattern · 문장 구조</span>
                  <p>{focusGuide.structureEn}</p>
                  <small>한국어 · {focusGuide.structureKo}</small>
                </div>
                <div>
                  <span>Key word or form · 단어·형태</span>
                  <p>{memoryFocus.en}</p>
                  <small>한국어 · {memoryFocus.ko}</small>
                </div>
                <div>
                  <span>Memory tip · 암기 팁</span>
                  <p>{focusGuide.tipEn}</p>
                  <small>한국어 · {focusGuide.tipKo}</small>
                </div>
              </div>
              {mistakeQuestions.length > 1 && (
                <button className="insight-review-link" onClick={() => openReview("practice")}>오답 {mistakeQuestions.length}문장 모두 보기 →</button>
              )}
            </>
          ) : (
            <>
              <h2>No mistakes today — your key patterns are becoming stable.</h2>
              <p>{strongestTag ?? "연결 표현"}을 자연스럽게 구별했어요. 내일은 새로운 상황으로 이어서 연습해요.</p>
            </>
          )}
        </section>

        <div className="summary-actions">
          <button className="primary-button" onClick={() => openReview("all")}>문장 다시 보기</button>
          <button className="secondary-button" onClick={shareResult}>결과 공유하기</button>
          <button className="quiet-button" onClick={resetLesson}>처음부터 다시 풀기</button>
        </div>
        {notice && <p className="toast" role="status">{notice}</p>}
      </main>
    );
  }

  if (view === "review") {
    const reviewQuestions = questions.filter((question) => {
      const record = answers[question.id];
      if (reviewFilter === "practice") return (answers[question.id]?.mistakes ?? 0) > 0;
      if (reviewFilter === "correct") return record?.latestCorrect && record.mistakes === 0;
      if (reviewFilter === "saved") return bookmarks.includes(question.id);
      return Boolean(record);
    });

    const selectedReviewQuestion = selectedReviewId ? questionMap.get(selectedReviewId) : undefined;

    if (selectedReviewQuestion) {
      const record = answers[selectedReviewQuestion.id];
      const guide = getGrammarGuide(selectedReviewQuestion);
      const wasCorrect = Boolean(record?.latestCorrect && record.mistakes === 0);

      return (
        <main className="app-shell review-shell">
          <header className="review-header">
            <button className="text-button" onClick={() => setSelectedReviewId(null)}>목록으로</button>
            <h1>문장 자세히 보기</h1>
            <button
              className={`icon-button ${bookmarks.includes(selectedReviewQuestion.id) ? "is-active" : ""}`}
              onClick={() => toggleBookmark(selectedReviewQuestion.id)}
              aria-label={bookmarks.includes(selectedReviewQuestion.id) ? "저장 취소" : "문장 저장"}
              aria-pressed={bookmarks.includes(selectedReviewQuestion.id)}
            >
              {bookmarks.includes(selectedReviewQuestion.id) ? "★" : "☆"}
            </button>
          </header>

          <section className="review-detail">
            <div className="review-detail-meta">
              <span>{selectedReviewQuestion.tag}</span>
              <span className={`status ${wasCorrect ? "familiar" : "practice"}`}>
                {wasCorrect ? "맞은 문장" : "다시 볼 문장"}
              </span>
            </div>
            {selectedReviewQuestion.context && (
              <p className="review-situation">{selectedReviewQuestion.context}</p>
            )}
            <h2>{selectedReviewQuestion.correctSentence}</h2>
            <button
              className="listen-full-button"
              onClick={() => speak(selectedReviewQuestion.correctSentence)}
            >
              정답 문장 듣기
            </button>

            {record && (
              <div className="answer-comparison">
                <span>내가 마지막으로 선택한 답</span>
                <p>{answerLabel(selectedReviewQuestion, record.lastAnswer)}</p>
              </div>
            )}

            <div className="learning-notes">
              <div>
                <span>Why this expression? · 선택 이유</span>
                <p>{selectedReviewQuestion.explanationEn}</p>
                <p className="korean-translation" lang="ko">한국어 · {selectedReviewQuestion.explanationKo}</p>
              </div>
              <div>
                <span>Sentence pattern · 문장 구조</span>
                <p className="structure-line">{guide.structureEn}</p>
                <p className="korean-translation structure-line" lang="ko">한국어 · {guide.structureKo}</p>
              </div>
              <div>
                <span>How to tell the difference · 표현 구별</span>
                <p>{guide.tipEn}</p>
                <p className="korean-translation" lang="ko">한국어 · {guide.tipKo}</p>
              </div>
            </div>
          </section>
        </main>
      );
    }

    return (
      <main className="app-shell review-shell">
        <header className="review-header">
          <button className="text-button" onClick={() => setView(completed ? "summary" : "home")}>돌아가기</button>
          <h1>문장 다시 보기</h1>
          <span className="header-spacer" />
        </header>

        <div className="filter-tabs" role="tablist" aria-label="복습 문장 필터">
          {([
            ["all", `전체 ${completedCount}`],
            ["practice", `틀림 ${practiceCount}`],
            ["correct", `맞음 ${familiarCount}`],
            ["saved", `저장 ${bookmarks.length}`],
          ] as const).map(([filter, label]) => (
            <button
              key={filter}
              role="tab"
              aria-selected={reviewFilter === filter}
              className={reviewFilter === filter ? "active" : ""}
              onClick={() => setReviewFilter(filter)}
            >
              {label}
            </button>
          ))}
        </div>

        <section className="review-list">
          {reviewQuestions.length === 0 && (
            <div className="empty-state">
              <strong>아직 여기에 문장이 없어요.</strong>
              <p>학습 중 별표를 누르거나 다시 볼 문장이 생기면 모아 드릴게요.</p>
            </div>
          )}
          {reviewQuestions.map((question) => {
            const record = answers[question.id];
            const status = !record
              ? "아직 안 봄"
              : record.mistakes === 0
                ? "익숙함"
                : record.latestCorrect
                  ? "조금 더 연습"
                  : "다시 보기";
            return (
              <article className="review-card" key={question.id}>
                <button className="review-card-open" onClick={() => setSelectedReviewId(question.id)}>
                  <div className="review-card-top">
                    <span>{question.tag}</span>
                    <span className={`status ${status === "익숙함" ? "familiar" : "practice"}`}>{status}</span>
                  </div>
                  {question.context && <p className="review-card-context">{question.context}</p>}
                  <h2>{question.correctSentence}</h2>
                  <span className="review-open-label">자세히 공부하기 →</span>
                </button>
              </article>
            );
          })}
        </section>
      </main>
    );
  }

  const plannedQuestions = queue.filter((item) => !item.review);
  const goalStageCounts = ([1, 2, 3] as const).map(
    (stage) => plannedQuestions.filter((item) => questionMap.get(item.questionId)?.stage === stage).length,
  );

  return (
    <main className="app-shell home-shell">
      <header className="brand-header">
        <div className="brand">
          <span className="brand-mark">가</span>
          <span>Abby Korean Study</span>
        </div>
        <div className="header-account">
          <span className="day-label">{sessionLabel}</span>
          <button onClick={logout} title="Log out">
            <strong>{authUser?.name}</strong>
            <span>Log out</span>
          </button>
        </div>
      </header>

      <section className="home-hero">
        <div className="hero-copy">
          <p className="eyebrow">매일 10분, 자연스럽게</p>
          <h1>단어를 아는 것에서<br />문장을 잇는 감각으로.</h1>
          <p className="hero-description">
            {isWeekend
              ? "오늘은 이번 주에 틀린 문장만 다시 풀며 헷갈린 구조와 표현을 확실히 익혀요."
              : "오늘은 ‘아마’의 뉘앙스를 익히고 두 행동을 자연스럽게 이어 말하는 방법을 연습해요."}
          </p>
        </div>
      </section>

      <section className="today-card">
        {!started && !isWeekend && (
          <div className="goal-setting">
            <div>
              <span className="card-label">오늘의 목표</span>
              <h2>몇 문항을 연습할까요?</h2>
              <p>30·40·50문항 중 오늘의 목표를 선택하세요.</p>
            </div>
            <div className="goal-options" role="group" aria-label="오늘의 목표 문항 수">
              {DAILY_GOAL_OPTIONS.map((goal) => (
                <button
                  key={goal}
                  className={dailyGoal === goal ? "active" : ""}
                  onClick={() => updateDailyGoal(goal)}
                  aria-pressed={dailyGoal === goal}
                >
                  <strong>{goal}</strong>
                  <span>문항</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="today-heading">
          <div>
            <span className="card-label">{isWeekend ? "주말 오답 복습" : "오늘의 코스"}</span>
            <h2>
              {isWeekend
                ? `${reviewPool.length}문장 · 틀린 문장만`
                : `${dailyGoal}문항 · 약 ${Math.max(10, Math.ceil(dailyGoal * 0.45))}분`}
            </h2>
          </div>
          {started && !completed && (
            <span className={`resume-badge ${syncState === "error" ? "sync-error" : ""}`}>
              {syncState === "saving" ? "서버 저장 중" : syncState === "error" ? "브라우저에 저장됨" : "서버 저장됨"}
            </span>
          )}
        </div>

        {isWeekend ? (
          <div className="weekend-course">
            <span className="round-number mint">↻</span>
            <div>
              <strong>이번 주 오답 다시 풀기</strong>
              <span>{reviewPool.length > 0 ? "맞힐 때까지 복습 목록에 남아요." : "평일 학습에서 틀린 문장이 생기면 여기에 모여요."}</span>
            </div>
          </div>
        ) : (
          <ol className="round-list">
            {Object.entries(stageMeta).map(([stage, meta], index) => (
              <li key={stage}>
                <span className={`round-number ${meta.tone}`}>{stage}</span>
                <div><strong>{meta.label}</strong><span>{meta.short} · {goalStageCounts[index]}문장</span></div>
              </li>
            ))}
          </ol>
        )}

        {!completed ? (
          <button className="primary-button large" onClick={beginLesson}>
            {started ? "이어서 연습하기" : isWeekend ? "오답 복습 시작하기" : "오늘의 연습 시작하기"}
          </button>
        ) : (
          <button className="primary-button large" onClick={() => setView("summary")}>
            오늘의 기록 보기
          </button>
        )}
        {notice && <p className="course-notice" role="status">{notice}</p>}
      </section>

      <section className="preference-row">
        <div>
          <strong>정답 문장 자동 듣기</strong>
          <span>원할 때만 켤 수 있어요</span>
        </div>
        <button
          className={`toggle ${autoAudio ? "on" : ""}`}
          onClick={() => setAutoAudio((current) => !current)}
          role="switch"
          aria-checked={autoAudio}
          aria-label="정답 문장 자동 듣기"
        ><span /></button>
      </section>

      {(started || completed) && (
        <div className="home-links">
          <button onClick={() => openReview("saved")}>저장한 문장 보기</button>
          <button onClick={resetLesson}>기록 초기화</button>
        </div>
      )}

      <footer>틀려도 괜찮아요. 같은 구조를 다시 만나며 익혀요.</footer>
    </main>
  );
}
